<template>
	<query-form v-model:query="query" :loading="loading" @apply="getReportData" @abort="clearQuery">
		<template #display>
			<div class="display-value">
				<div class="label">Date Range:</div>
				<div class="value">{{ formatDate(query.date_range, 'ddd, MMM D, YYYY') }}</div>
			</div>
			<div class="display-value">
				<div class="label">Grouping:</div>
				<div class="value">{{ displayLabel(query.grouping, grouping_options) }}</div>
			</div>
		</template>
		<template #utils>
			<p-button
				v-tooltip.top="'Refresh'"
				icon="pi pi-refresh"
				aria-label="Refresh"
				class="mr-2"
				@click="getReportData(query, true)"
			/>
		</template>
		<template #form="form">
			<row>
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Date Range:</label>
							<div class="controls">
								<div class="field">
									<date-range-picker v-model="form.query.date_range" :date-options="date_picker_options" />
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Grouping:</label>
							<div class="controls">
								<div class="field">
									<radio-group v-model="form.query.grouping" :options="grouping_options" stacked />
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column></column>
			</row>
		</template>
	</query-form>
	<p-select-button v-model="aggregate" :options="aggregate_options" option-label="label" option-value="value" />
	<gutter size="20px" />
	<AMSalesMasterReport :raw-report-data="am_data" :aggregate="aggregate" />
	<gutter size="20px" />
	<AESalesMasterReport :raw-report-data="ae_data" :aggregate="aggregate" />
	<gutter size="20px" />
	<SASalesMasterReport :raw-report-data="sa_data" :aggregate="aggregate" />
	<gutter size="20px" />
	<LTASalesMasterReport :raw-report-data="lta_data" :aggregate="aggregate" />
</template>

<script>
import pCalendar from 'primevue/calendar';
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import lineLoader from '@/components/elements/LineLoader.vue';
import radioGroup from '@/components/forms/RadioGroup.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import quickFilter from '@/components/widgets/QuickFilter.vue';
import AMSalesMasterReport from './AMSalesMasterReport.vue';
import AESalesMasterReport from './AESalesMasterReport.vue';
import LTASalesMasterReport from './LTASalesMasterReport.vue';
import SASalesMasterReport from './SASalesMasterReport.vue';
import { displayLabel, displaySelected, formatDate } from '@/lib/Filters';
import { reportSalesMaster } from '@GQL';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import { cloneDeep } from 'lodash-es';

dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

export default {
	name: 'SalesMasterReportTable',
	components: {
		dateRangePicker,
		lineLoader,
		pCalendar,
		queryForm,
		quickFilter,
		radioGroup,
		AESalesMasterReport,
		AMSalesMasterReport,
		LTASalesMasterReport,
		SASalesMasterReport,
	},
	data() {
		return {
			loading: false,
			query: {
				date_range: [dayjs().startOf('year').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
				grouping: 'day',
			},
			aggregate: 'totals',
			am_data: [],
			ae_data: [],
			sa_data: [],
			lta_data: {},
			grouping_options: [
				{ label: 'Day', value: 'day' },
				{ label: 'Week', value: 'week' },
				{ label: 'Month', value: 'month' },
			],
			aggregate_options: [
				{ label: 'Totals', value: 'totals' },
				{ label: 'Averages', value: 'averages' },
			],
			date_picker_options: [
				{
					label: 'This Week',
					icon: 'calendar-today',
					value: [dayjs().startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				},
				{
					label: 'This Month',
					icon: 'calendar-today',
					value: [dayjs().startOf('month').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
				},
				{
					label: 'Last Month',
					icon: 'calendar-today',
					value: [
						dayjs().subtract(1, 'month').startOf('month').toDate(),
						dayjs().subtract(1, 'month').endOf('month').toDate(),
					],
				},
				{
					label: 'Last 4 Weeks',
					icon: 'calendar-today',
					value: [dayjs().subtract(3, 'weeks').startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last 6 Months',
					value: [
						dayjs().subtract(5, 'months').startOf('month').toDate(),
						dayjs().subtract(1, 'day').endOf('day').toDate(),
					],
				},
				{
					icon: 'calendar-today',
					label: 'Last 12 Months',
					value: [
						dayjs().subtract(11, 'months').startOf('month').toDate(),
						dayjs().subtract(1, 'day').endOf('day').toDate(),
					],
				},
				{
					icon: 'calendar-today',
					label: 'This Calendar Year',
					value: [dayjs().startOf('year').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last Calendar Year',
					value: [
						dayjs().startOf('year').subtract(1, 'year').toDate(),
						dayjs().subtract(1, 'year').endOf('year').toDate(),
					],
				},
			],
		};
	},
	methods: {
		displayLabel,
		displaySelected,
		formatDate,
		async getReportData(query, refresh) {
			const report_query = cloneDeep(query);

			try {
				this.loading = true;
				const { reportAMSalesMaster, reportAESalesMaster, reportSASalesMaster, reportLTASalesMaster } =
					await reportSalesMaster(report_query, refresh);

				this.am_data = reportAMSalesMaster;
				this.ae_data = reportAESalesMaster;
				this.sa_data = reportSASalesMaster;
				this.lta_data = reportLTASalesMaster;
			} finally {
				this.loading = false;

				// Scroll all the way right
				this.$nextTick(() => {
					const tables = document.querySelectorAll('.p-datatable-wrapper');
					for (let i = 0; i < tables.length; i++) {
						tables[i].scrollLeft = tables[i].scrollWidth;
					}
				});
			}
		},
		clearQuery() {
			this.loading = false;
		},
	},
	async mounted() {
		await this.getReportData(this.query);
	},
};
</script>

<style lang="less" scoped>
:deep(.p-card-header) {
	border-bottom: 1px solid var(--gray-10);
	cursor: default;
	height: 50px;
	padding: 0 1.5em;

	h2 {
		font-size: 1.125em;
		margin-bottom: 0;
	}
}

:deep(.p-card.collapsed) {
	.p-card-body {
		display: none;
	}

	.expander .mdi-icon {
		transform: rotate(-90deg);
	}
}

:deep(.master-report-table) {
	.p-datatable-tbody td {
		padding: 0;
	}

	.data-cell {
		border-bottom: 1px solid var(--gray-15);
		padding: 0.625rem 1rem;
	}
}

:deep(.p-datatable) {
	.p-datatable-tbody > tr.p-rowgroup-header {
		background-color: var(--gray-90);
		font-weight: bold;

		.sticky-heading {
			height: 40px;
			left: 0;
			// line-height: 40px;
			padding: 0.625rem 1rem;
			position: sticky;
			max-width: 500px;
		}

		td {
			color: white;
			padding: 0;
		}
	}
}

:deep(.p-datatable-emptymessage) {
	td {
		padding: 0.625rem 1rem !important;
	}
}

:deep(.p-frozen-column):nth-child(2) {
	.data-cell {
		border-right: 1px solid var(--gray-15);
	}
}

:deep(.width-hack) {
	padding: 0 !important;
	width: 0;
}
</style>
